import { useCallback, useEffect, useRef, useState } from "react";
import type { SocialCommentPlatformFilter, SocialPostListItemDto } from "@multi-chat/shared";
import { listSocialPosts, SOCIAL_POST_PAGE_SIZE } from "../../../api/social-comments-api.js";
import { useInboxRealtime } from "../InboxRealtimeProvider.js";

export type SocialPostsListFilters = {
  platform?: SocialCommentPlatformFilter;
  search?: string;
  hasOpenComments?: boolean;
};

export type SocialPostsListView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "empty" }
  | { kind: "searchEmpty" }
  | { kind: "ready"; items: SocialPostListItemDto[] };

export function useSocialPostsList(
  organizationId: number,
  filters: SocialPostsListFilters,
  unreachableMessage: string,
  enabled = true,
): {
  listView: SocialPostsListView;
  hasMore: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  loadMore: () => boolean;
  refresh: () => void;
} {
  const { subscribeSocialCommentChanged, subscribeReconnect } = useInboxRealtime();
  const [items, setItems] = useState<SocialPostListItemDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const orgRef = useRef(organizationId);
  const enabledRef = useRef(enabled);
  const filtersKey = JSON.stringify(filters);

  const fetchPage = useCallback(
    async (mode: "replace" | "append", cursor?: string) => {
      if (!enabledRef.current) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const orgId = organizationId;
      if (mode === "replace") {
        setRefreshing(items.length > 0);
        if (items.length === 0) setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await listSocialPosts(orgId, {
          platform: filters.platform,
          search: filters.search,
          hasOpenComments: filters.hasOpenComments,
          cursor,
          limit: SOCIAL_POST_PAGE_SIZE,
          signal: controller.signal,
        });
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        setItems((current) =>
          mode === "append" ? [...current, ...response.items] : response.items,
        );
        setNextCursor(response.nextCursor);
        setError(null);
      } catch {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        if (mode === "replace" && items.length === 0) {
          setError(unreachableMessage);
        }
      } finally {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    },
    [filters, items.length, organizationId, unreachableMessage],
  );

  const refresh = useCallback(() => {
    void fetchPage("replace");
  }, [fetchPage]);

  useEffect(() => {
    enabledRef.current = enabled;
    orgRef.current = organizationId;
    if (!enabled) {
      abortRef.current?.abort();
      setLoading(false);
      return;
    }
    setItems([]);
    setNextCursor(null);
    setLoading(true);
    setError(null);
    void fetchPage("replace");
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when org/filters/enabled change
  }, [organizationId, filtersKey, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const unsubEvent = subscribeSocialCommentChanged(() => {
      void fetchPage("replace");
    });
    const unsubReconnect = subscribeReconnect(() => {
      void fetchPage("replace");
    });
    return () => {
      unsubEvent();
      unsubReconnect();
    };
  }, [enabled, fetchPage, subscribeReconnect, subscribeSocialCommentChanged]);

  const loadMore = useCallback((): boolean => {
    if (!nextCursor || loadingMore || loading) return false;
    void fetchPage("append", nextCursor);
    return true;
  }, [fetchPage, loading, loadingMore, nextCursor]);

  let listView: SocialPostsListView;
  if (loading && items.length === 0) {
    listView = { kind: "loading" };
  } else if (error && items.length === 0) {
    listView = { kind: "error", message: error };
  } else if (items.length === 0) {
    listView =
      filters.search != null && filters.search.trim() !== ""
        ? { kind: "searchEmpty" }
        : { kind: "empty" };
  } else {
    listView = { kind: "ready", items };
  }

  return {
    listView,
    hasMore: nextCursor != null,
    loadingMore,
    refreshing,
    loadMore,
    refresh,
  };
}
